import { BotIcon, CircleHelp, LogOut, type LucideIcon } from "lucide-react"
import * as React from "react"

import { ChatSidebarItem } from "@/components/feature/ChatHistory/ChatSidebarItem"
import { CreditsCard } from "@/components/feature/FreeTrial/CreditsCard"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarRail,
} from "@/components/ui/sidebar"
import {
  MC_VIEW_PERMISSIONS,
  PERMISSIONS,
  type PermissionKey,
} from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { HELP_BASE } from "@/features/helpdesk"
import { useOnboarding } from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useGetCompanyProfileStatus } from "@/services/api/companyInfoService"
import {
  IconBuildingSkyscraper as IconBuildingBank,
  IconDatabase,
  IconFileStack,
  IconFileUpload,
  IconPlug,
  IconProps,
  IconRobot,
  IconSettings,
  IconShieldCheck,
  IconUser,
} from "@tabler/icons-react"
import { useNavigate } from "react-router"
import { FreeTrialCard } from "./feature/FreeTrial/FreeTrialComposed"
import { UsageTracker } from "./feature/FreeTrial/UsageTracker"
import { LanguageSelector } from "./LanguageSelector"
import { SidebarFooterOption } from "./layout/SidebarFooterOption"
import { MaterialComplianceSidebarGroup } from "./MaterialComplianceSidebarGroup"
import { SidebarLogo } from "./SidebarLogo"
import { Separator } from "./ui/separator"

type AppNavItem = {
  id: string
  title: string
  path: string
  icon: React.ComponentType<IconProps> | LucideIcon
  showDot?: boolean
  /** VIEW permission gate. Omitted for entries anyone should see. */
  requiresPermission?: PermissionKey
}

export function AppSidebar({
  isSidebarOpen,
  isSidebarLockedOpen = false,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  isSidebarOpen?: boolean
  isSidebarLockedOpen?: boolean
}) {
  const { translate } = useTranslation()
  const { user, handleLogout, hasPermission } = useUser()
  const navigate = useNavigate()
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const helpdeskEnabled = useFeatureFlag("HELPDESK")
  const complianceEnabled = useFeatureFlag("COMPLIANCE")
  const materialComplianceEnabled = useFeatureFlag("MATERIAL_COMPLIANCE")
  const { handleHighlightedAction } = useOnboarding()
  const companyProfileEnabled = useFeatureFlag("COMPANY_PROFILE")
  const { data: companyProfileStatus } = useGetCompanyProfileStatus({
    enabled: companyProfileEnabled,
  })

  const showCompanyInfoDot = companyProfileStatus?.state === "ready"
  // MC parent group appears only if the member can view at least ONE of the
  // studios inside it. `hasPermission` is kill-switch-aware (TEAM_MANAGEMENT
  // off → returns true → group always shows, backward compat).
  const showMcGroup = MC_VIEW_PERMISSIONS.some((p) => hasPermission(p))

  const navItems1 = React.useMemo<AppNavItem[]>(
    () => [
      {
        id: "navItem1",
        title: translate("NAV_UPLOAD"),
        path: "/questionnaire/upload",
        icon: IconFileUpload,
        requiresPermission: PERMISSIONS.QUEST_VIEW,
      },
      {
        id: "navItem2",
        title: translate("NAV_HISTORY"),
        path: "/history",
        icon: IconFileStack,
        requiresPermission: PERMISSIONS.QUEST_VIEW,
      },
    ],
    [translate],
  )

  const navItems2 = React.useMemo<AppNavItem[]>(
    () => [
      {
        id: "navItem3",
        title: translate("NAV_KNOWLEDGEBASE"),
        path: "/knowledgebase",
        icon: IconDatabase,
        requiresPermission: PERMISSIONS.KB_VIEW,
      },
      ...(materialComplianceEnabled && showMcGroup
        ? [
            {
              id: "navItemMaterialCompliance",
              title: translate("NAV_MATERIAL_COMPLIANCE"),
              path: "/material-compliance",
              icon: IconShieldCheck,
            },
          ]
        : []),
      ...(complianceEnabled
        ? [
            {
              id: "navItem5",
              title: translate("NAV_AUTOPILOTS"),
              path: "/qms-database",
              icon: IconRobot,
            },
            {
              id: "navItem6",
              title: translate("NAV_INTEGRATIONS"),
              path: "/integrations",
              icon: IconPlug,
              requiresPermission: PERMISSIONS.INTEGRATIONS_VIEW,
            },
          ]
        : []),
      ...(companyProfileEnabled && companyProfileStatus?.hasProfile
        ? [
            {
              id: "navItem7",
              title: translate("COMPANY_INFO"),
              path: "/company-info",
              icon: IconBuildingBank,
              showDot: showCompanyInfoDot,
            },
          ]
        : []),
    ],
    [
      translate,
      materialComplianceEnabled,
      showMcGroup,
      complianceEnabled,
      companyProfileEnabled,
      companyProfileStatus?.hasProfile,
      showCompanyInfoDot,
    ],
  )

  // Single filter for both nav-item groups. Kill-switch inherits via
  // hasPermission — TEAM_MANAGEMENT off → every entry passes.
  const isAllowed = React.useCallback(
    (item: AppNavItem) =>
      !item.requiresPermission || hasPermission(item.requiresPermission),
    [hasPermission],
  )
  const visibleNavItems1 = navItems1.filter(isAllowed)
  const visibleNavItems2 = navItems2.filter(isAllowed)

  const handleNavigation = (path: string, elementId?: string) => {
    if (elementId) {
      handleHighlightedAction(elementId)
    }
    navigate(path)
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarLogo />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {visibleNavItems1.map((item: AppNavItem) => {
              return (
                <SidebarFooterOption
                  key={item.title}
                  id={item.id}
                  title={item.title}
                  icon={item.icon}
                  onClick={() => handleNavigation(item.path, item.id)}
                  showDot={item.showDot}
                />
              )
            })}

            {chatHistoryEnabled ? (
              hasPermission(PERMISSIONS.CHAT_USE) ? (
                <ChatSidebarItem isSidebarOpen={isSidebarOpen} />
              ) : null
            ) : hasPermission(PERMISSIONS.CHAT_USE) ? (
              <SidebarFooterOption
                id="navItem4"
                title={translate("AI_CHAT/ASSISTANT")}
                icon={BotIcon}
                onClick={() => handleNavigation("/chat", "navItem4")}
              />
            ) : null}

            {visibleNavItems2.map((item) => {
              if (item.id === "navItemMaterialCompliance") {
                return (
                  <MaterialComplianceSidebarGroup
                    key={item.id}
                    title={item.title}
                  />
                )
              }
              return (
                <SidebarFooterOption
                  key={item.title}
                  id={item.id}
                  title={item.title}
                  icon={item.icon}
                  onClick={() => handleNavigation(item.path, item.id)}
                  showDot={item.showDot}
                />
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="pb-4">
        {helpdeskEnabled ? (
          <SidebarFooterOption
            id="navHelp"
            title={translate("SIDEBAR/HELP")}
            icon={CircleHelp}
            onClick={() => navigate(HELP_BASE)}
          />
        ) : null}
        <Separator />
        {isSidebarOpen ? <FreeTrialCard.DaysLimited /> : null}
        {isSidebarOpen ? <UsageTracker /> : null}
        {isSidebarOpen ? <CreditsCard /> : null}
        <SidebarFooterOption
          title={user?.username || ""}
          icon={IconUser}
          onClick={() => navigate("/settings")}
        />
        <SidebarFooterOption
          id="navSettings"
          title={translate("SETTINGS/TITLE")}
          icon={IconSettings}
          onClick={() => navigate("/settings")}
        />
        <LanguageSelector />
        <SidebarFooterOption
          title={translate("SIDEBAR/LOGOUT")}
          icon={LogOut}
          onClick={handleLogout}
        />
      </SidebarFooter>
      {!isSidebarLockedOpen && <SidebarRail />}
    </Sidebar>
  )
}
